import { relativeTime } from 'base/helpers';
import { ServiceHttpError } from 'base/service_error';
import { Button } from 'base/ui/button';
import { Dialog } from 'base/ui/dialog';
import { formatError } from 'base/ui/form';
import { menuItem } from 'base/ui/menu_model';
import { ToastQueue } from 'base/ui/toast';
import { TextRegularLight } from 'base/ui/typography';
import { CollabSyncer } from 'blocks/sync/collab_syncer';
import { computedFn } from 'mobx-utils';
import React from 'react';
import { Project403Dialog, Project404Dialog } from '../error_dialogs';

const SAVING_TOAST_GROUP = Symbol('saving-toast-group');

export function saveShortcutHandler(
  sync: CollabSyncer,
  openAuthWall: (() => void) | undefined,
  toastQueue: ToastQueue,
) {
  if (openAuthWall) {
    openAuthWall();
  }

  if (!sync.hasUnsavedChanges) {
    toastQueue.push({
      label: 'All changes saved',
      group: SAVING_TOAST_GROUP,
    });
    return;
  }

  toastQueue.push({
    label: 'Saving…',
    group: SAVING_TOAST_GROUP,
  });
  sync.syncNow().then(() => {
    toastQueue.push({
      label: 'All changes saved',
      group: SAVING_TOAST_GROUP,
    });
  });
}

export const saveMenuItem = computedFn(
  (sync: CollabSyncer, openAuthWall: (() => void) | undefined) => {
    if (openAuthWall) {
      return menuItem({ label: 'Save Project', onClick: openAuthWall });
    }
    if (sync.hasUnsavedChanges) {
      return menuItem({ label: 'Saving Project...', disabled: true });
    }
    return menuItem({
      label: `Saved (${relativeTime(sync.lastSyncedTimeS).toLowerCase()})`,
      disabled: true,
    });
  },
);

export function SyncFatalErrorDialog({
  error,
  hasUnsavedChanges,
}: {
  error: unknown;
  hasUnsavedChanges: boolean;
}) {
  if (error instanceof ServiceHttpError && error.status === 404) {
    return <Project404Dialog />;
  }
  if (error instanceof ServiceHttpError && error.status === 403) {
    return <Project403Dialog />;
  }
  if (
    error instanceof ServiceHttpError &&
    error.status === 409 &&
    (error.message.includes('base version requested does not match') ||
      error.message.includes('Your session was terminated') ||
      error.message.includes('The session has expired')) &&
    !hasUnsavedChanges
  ) {
    return <ReloadToReconnectDialog />;
  }
  return (
    <Dialog
      title="Disconnected"
      buttons={
        <Button
          variant="primary"
          label="Refresh"
          onClick={refreshPage}
          size="large"
        />
      }
    >
      <TextRegularLight>{formatError(error)}</TextRegularLight>
    </Dialog>
  );
}

function ReloadToReconnectDialog() {
  React.useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        refreshPage();
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => void document.removeEventListener('visibilitychange', handler);
  }, []);

  // Ideally the user never sees this dialog because of the visibilitychange handler above.
  return (
    <Dialog
      title="Disconnected"
      buttons={
        <>
          <Button
            variant="primary"
            label="Connect"
            onClick={refreshPage}
            size="large"
          />
        </>
      }
    >
      <TextRegularLight>
        All changes have been saved, but we lost internet for a moment.
      </TextRegularLight>
    </Dialog>
  );
}

function refreshPage() {
  window.location.reload();
}
